// ============================================================
//  BEREAN NATION — Main App Component
//  Full-featured: KJV Bible, Prayer Wall, Livestream,
//  Church Fathers, BN News — all backed by Firebase.
// ============================================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fetchChapter, prefetchBook, ALL_BOOKS, getBookInfo } from './bibleApi';
import {
  subscribePrayers, addPrayer, updatePrayer, deletePrayer,
  prayForRequest, markAnswered,
  subscribeAnnotations, saveAnnotation, deleteAnnotation,
} from './firestoreService';
import { ensureAuth } from './firebase';
import './App.css';

// ── Rumble channel configuration ──────────────────────────
// These are your real Rumble channels — used for "watch more" links
// and the livestream embed placeholder throughout the app.
export const RUMBLE_CHANNELS = {
  livestream: {
    name: 'Berean Nation (Main Channel)',
    url: 'https://rumble.com/c/BereanNation',
  },
  fathers: {
    name: 'Church Fathers Readings',
    url: 'https://rumble.com/c/c-7760294',
  },
  news: {
    name: 'Berean Nation News',
    url: 'https://rumble.com/c/c-7886792',
  },
};

// ════════════════════════════════════════════════════════
//  📝 MANUAL VIDEO LIST — EDIT THIS SECTION TO ADD NEW VIDEOS
// ════════════════════════════════════════════════════════
//  Since free Rumble auto-feed tools have proven unreliable,
//  these three lists are updated by hand. To add a new video:
//
//    1. Go to the video on Rumble, click "Share" → copy the link
//    2. Add a new { } entry to the matching list below
//    3. Fill in title, date, duration (optional), and the link
//    4. Save — the new video appears on that page
//
//  "link" should be the full Rumble video URL, e.g.:
//    https://rumble.com/v6abcde-some-video-title.html
//
//  Leave link as the channel URL if you haven't copied the
//  specific video link yet — it'll still take visitors to Rumble.
//
//  Example entry:
//    {
//      id: 'cf1',
//      title: 'Ignatius of Antioch — Letter to the Ephesians',
//      date: 'Jun 10, 2025',
//      duration: '38:24',
//      link: 'https://rumble.com/c/c-7760294',
//    },
// ════════════════════════════════════════════════════════

// Empty for now — add real videos here when ready. Until then,
// the page shows just the "Watch on Rumble" channel button.
const CHURCH_FATHERS_VIDEOS = [
  // ↑ Add new Church Fathers videos here ↑
];

// Empty for now — add real videos here when ready. Until then,
// the page shows just the "Watch on Rumble" channel button.
const BN_NEWS_VIDEOS = [
  // ↑ Add new Berean Nation News videos here ↑
];

const ARCHIVED_STREAMS = [
  {
    id: 'as1',
    title: 'Thursday Livestream Archive',
    date: 'Jun 12, 2025',
    duration: '1:24:33',
    link: 'https://rumble.com/c/BereanNation',
  },
  {
    id: 'as2',
    title: 'Friday Livestream Archive',
    date: 'Jun 13, 2025',
    duration: '58:14',
    link: 'https://rumble.com/c/BereanNation',
  },
  // ↑ Add new archived livestreams above this line ↑
];
// ════════════════════════════════════════════════════════
//  END MANUAL VIDEO LIST
// ════════════════════════════════════════════════════════
const SEED_CHAT = [
  { user:'BrotherTom',  text:'Praise God, glad to be here tonight!' },
  { user:'SisterRuth',  text:'Blessings to everyone joining from across the nation!' },
  { user:'Moderator',   text:'Stream starts in 5 minutes. Share with your friends!' },
  { user:'JohnB',       text:'Tuning in from Texas — God bless Berean Nation 🙏' },
];

export default function App() {
  // ── Navigation ─────────────────────────────────────────
  const [tab, setTab] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('tab') || 'bible';
  });

  // ── Bible state ────────────────────────────────────────
  const [book,       setBook]       = useState('Genesis');
  const [chapter,    setChapter]    = useState(1);
  const [verses,     setVerses]     = useState([]);
  const [bibleLoading, setBibleLoading] = useState(false);
  const [bibleError,   setBibleError]   = useState(null);
  const [highlights, setHighlights] = useState({});  // verseIndex → color
  const [notes,      setNotes]      = useState({});  // verseIndex → { text, docId }
  const [activeHl,   setActiveHl]   = useState('yellow');
  const [selectedVerse, setSelectedVerse] = useState(null);
  const [noteInput,  setNoteInput]  = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [testament,  setTestament]  = useState('OT');

  // ── Prayer state ───────────────────────────────────────
  const [prayers,    setPrayers]    = useState([]);
  const [showModal,  setShowModal]  = useState(false);
  const [editPrayer, setEditPrayer] = useState(null);
  const [prayerForm, setPrayerForm] = useState({ name:'', request:'', tags:'', anonymous: false });
  const [prayerFilter, setPrayerFilter] = useState('all');

  // ── Video state ────────────────────────────────────────
  // Video lists come straight from the manual lists above — no live
  // sync needed since free Rumble auto-feed tools proved unreliable.
  // To update videos, edit CHURCH_FATHERS_VIDEOS / BN_NEWS_VIDEOS above.
  const fathersVideos = CHURCH_FATHERS_VIDEOS;
  const newsVideos    = BN_NEWS_VIDEOS;

  // ── Chat state ────────────────────────────────────────
  const [chatMsgs,  setChatMsgs]  = useState(SEED_CHAT);
  const [chatInput, setChatInput] = useState('');
  const chatRef = useRef(null);

  // ── Notification state ────────────────────────────────
  const [notifs,      setNotifs]      = useState([]);
  // ── User ──────────────────────────────────────────────
  const [userId, setUserId] = useState(null);

  // ═════════════════════════════════════════════════════
  //  Effects
  // ═════════════════════════════════════════════════════

  // Auth
  useEffect(() => {
    ensureAuth().then(user => setUserId(user.uid));
  }, []);

  // Bible: fetch chapter
  useEffect(() => {
    let cancelled = false;
    setBibleLoading(true);
    setBibleError(null);
    setSelectedVerse(null);
    fetchChapter(book, chapter)
      .then(v => { if (!cancelled) { setVerses(v); setBibleLoading(false); } })
      .catch(e => { if (!cancelled) { setBibleError(e.message); setBibleLoading(false); } });
    return () => { cancelled = true; };
  }, [book, chapter]);

  // Bible: prefetch whole book in background
  useEffect(() => {
    prefetchBook(book);
  }, [book]);

  // Bible: annotations from Firestore
  useEffect(() => {
    if (!userId) return;
    let unsub;
    subscribeAnnotations(book, chapter, annMap => {
      const hl = {}, nt = {};
      Object.entries(annMap).forEach(([vi, val]) => {
        if (val.highlight) hl[vi] = val.highlight;
        if (val.note)      nt[vi] = { text: val.note, docId: val.docId };
      });
      setHighlights(hl);
      setNotes(nt);
    }).then(fn => { unsub = fn; });
    return () => { if (unsub) unsub(); };
  }, [book, chapter, userId]);

  // Prayers: real-time
  useEffect(() => {
    const unsub = subscribePrayers(setPrayers);
    return unsub;
  }, []);

  // ═════════════════════════════════════════════════════
  //  Helpers
  // ═════════════════════════════════════════════════════

  const pushNotif = useCallback((title, body) => {
    const id = Date.now();
    setNotifs(n => [...n, { id, title, body }]);
    setTimeout(() => setNotifs(n => n.filter(x => x.id !== id)), 4500);
  }, []);

  // Bible interactions
  const handleVerseClick = (i) => {
    setSelectedVerse(prev => prev === i ? null : i);
    setNoteInput(notes[i]?.text || '');
  };

  const handleHighlight = async (i) => {
    const current = highlights[i];
    const newColor = current === activeHl ? null : activeHl;
    await saveAnnotation({ book, chapter, verseIndex: i, highlight: newColor, note: notes[i]?.text || null });
  };

  const handleSaveNote = async () => {
    if (selectedVerse === null || !noteInput.trim()) return;
    await saveAnnotation({ book, chapter, verseIndex: selectedVerse, highlight: highlights[selectedVerse] || null, note: noteInput.trim() });
    pushNotif('Note Saved', `${book} ${chapter}:${selectedVerse + 1}`);
    setSelectedVerse(null);
    setNoteInput('');
  };

  const handleDeleteNote = async (vi) => {
    const docId = notes[vi]?.docId;
    if (docId) await deleteAnnotation(docId);
    else await saveAnnotation({ book, chapter, verseIndex: vi, highlight: highlights[vi] || null, note: null });
  };

  // Prayer interactions
  const handlePrayerSubmit = async () => {
    if (!prayerForm.request.trim()) return;
    const tags = prayerForm.tags.split(',').map(t => t.trim()).filter(Boolean);
    if (editPrayer) {
      await updatePrayer(editPrayer.id, { name: prayerForm.name || 'Anonymous', request: prayerForm.request, tags });
      pushNotif('Prayer Updated', 'Your request has been updated for the congregation');
    } else {
      await addPrayer({ name: prayerForm.name, request: prayerForm.request, tags, isAnonymous: prayerForm.anonymous });
    }
    closeModal();
  };

  const closeModal = () => {
    setShowModal(false); setEditPrayer(null);
    setPrayerForm({ name:'', request:'', tags:'', anonymous: false });
  };

  const openEditPrayer = (pr) => {
    setEditPrayer(pr);
    setPrayerForm({ name: pr.name, request: pr.request, tags: pr.tags?.join(', ') || '', anonymous: false });
    setShowModal(true);
  };

  const copyShareLink = (id) => {
    navigator.clipboard.writeText(`https://bereannation.com/prayer/${id}`);
    pushNotif('Link Copied', 'Share this prayer request with others');
  };

  const handleMarkAnswered = async (pr) => {
    await markAnswered(pr.id, !pr.answered);
    if (!pr.answered) pushNotif('🎉 Praise Report!', `Glory to God — ${pr.name}'s prayer was answered!`);
  };

  // Chat
  const sendChat = () => {
    if (!chatInput.trim()) return;
    setChatMsgs(m => [...m, { user: 'You', text: chatInput }]);
    setChatInput('');
    setTimeout(() => { if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight; }, 80);
  };

  // Derived
  const bookInfo = getBookInfo(book);
  const filteredVerses = searchTerm
    ? verses.map((v, i) => ({ v, i })).filter(({ v }) => v.toLowerCase().includes(searchTerm.toLowerCase()))
    : verses.map((v, i) => ({ v, i }));

  const filteredPrayers = prayers.filter(p => {
    if (prayerFilter === 'answered')   return p.answered;
    if (prayerFilter === 'unanswered') return !p.answered;
    if (prayerFilter === 'mine')       return p.authorUid === userId;
    return true;
  });

  const today = new Date();
  const dow   = today.getDay();
  const isLiveDay = dow === 4 || dow === 5;

  const OT_BOOKS = ALL_BOOKS.filter((_, i) => i < 39);
  const NT_BOOKS = ALL_BOOKS.filter((_, i) => i >= 39);
  const displayBooks = testament === 'OT' ? OT_BOOKS : NT_BOOKS;

  // ═════════════════════════════════════════════════════
  //  Render
  // ═════════════════════════════════════════════════════
  return (
    <div className="app">

      {/* ── Header ── */}
      <header className="header">
        <div className="logo">
          ✦ Berean Nation
          <span>STUDY · PRAY · WATCH</span>
        </div>
        <nav className="nav">
          {[['bible','✦ KJV Bible'],['prayer','🙏 Prayer Wall'],['livestream','📡 Livestream'],['fathers','📖 Church Fathers'],['news','📰 BN News']].map(([t, label]) => (
            <button key={t} className={`nav-btn${tab === t ? ' active' : ''}`} onClick={() => setTab(t)}>{label}</button>
          ))}
        </nav>
      </header>

      {/* ════════════════════════════════════
           BIBLE TAB
      ════════════════════════════════════ */}
      {tab === 'bible' && (
        <div className="main">
          <div className="bible-panel">

            {/* Toolbar */}
            <div className="bible-toolbar">
              <div className="testament-toggle">
                <button className={`tt-btn${testament==='OT'?' active':''}`} onClick={() => setTestament('OT')}>Old Testament</button>
                <button className={`tt-btn${testament==='NT'?' active':''}`} onClick={() => setTestament('NT')}>New Testament</button>
              </div>
              <select className="bible-select" value={book} onChange={e => { setBook(e.target.value); setChapter(1); }}>
                {displayBooks.map(b => <option key={b.name} value={b.name}>{b.name}</option>)}
              </select>
              <select className="bible-select" value={chapter} onChange={e => setChapter(Number(e.target.value))}>
                {Array.from({ length: bookInfo?.chapters || 1 }, (_, i) => i + 1).map(c => (
                  <option key={c} value={c}>Chapter {c}</option>
                ))}
              </select>
              <input className="search-box" placeholder="Search chapter…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
              <div className="hl-controls">
                <span className="hl-label">Highlight:</span>
                {['yellow','blue','green','pink','none'].map(c => (
                  <div key={c} className={`hl-dot hl-${c}${activeHl===c?' active':''}`} onClick={() => setActiveHl(c)} title={c} />
                ))}
              </div>
            </div>

            {/* Chapter nav */}
            <div className="chapter-nav">
              <button className="ch-arrow" onClick={() => setChapter(c => Math.max(1, c - 1))} disabled={chapter <= 1}>‹</button>
              <span className="ch-label">{book} — Chapter {chapter}</span>
              <button className="ch-arrow" onClick={() => setChapter(c => Math.min(bookInfo?.chapters || 1, c + 1))} disabled={chapter >= (bookInfo?.chapters || 1)}>›</button>
            </div>

            {/* Verses */}
            <div className="bible-content">
              {bibleLoading && <div className="loading">Loading {book} {chapter}…</div>}
              {bibleError && <div className="error">Could not load chapter. Check your connection.<br /><small>{bibleError}</small></div>}
              {!bibleLoading && !bibleError && filteredVerses.map(({ v, i }) => {
                const hl    = highlights[i];
                const hasNote = !!notes[i];
                return (
                  <div
                    key={i}
                    className={`verse${hl ? ` hl-${hl}` : ''}${hasNote ? ' has-note' : ''}${selectedVerse === i ? ' selected' : ''}`}
                    onClick={() => handleVerseClick(i)}
                  >
                    <span className="verse-num">{i + 1}</span>
                    <span className="verse-text">{v}</span>
                    {selectedVerse === i && (
                      <button className="hl-btn" onClick={e => { e.stopPropagation(); handleHighlight(i); }}>
                        ✎ Highlight
                      </button>
                    )}
                    {hasNote && <div className="note-indicator" title={notes[i].text}>📝</div>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Annotation Panel */}
          <div className="annotation-panel">
            <div className="panel-title">✦ My Annotations</div>

            {/* Selected verse note editor */}
            {selectedVerse !== null && (
              <div className="note-editor">
                <div className="note-editor-label">{book} {chapter}:{selectedVerse + 1}</div>
                <textarea
                  className="note-input"
                  placeholder="Write your note or cross-reference…"
                  value={noteInput}
                  onChange={e => setNoteInput(e.target.value)}
                />
                <button className="note-save-btn" onClick={handleSaveNote}>Save Note</button>
                <button className="note-cancel-btn" onClick={() => setSelectedVerse(null)}>Cancel</button>
              </div>
            )}

            <div className="ann-list">
              {Object.entries(notes).length === 0 && (
                <div className="ann-empty">Click any verse to add a highlight or note</div>
              )}
              {Object.entries(notes).map(([vi, val]) => (
                <div key={vi} className="ann-item">
                  <div className="ann-ref">{book} {chapter}:{Number(vi) + 1}</div>
                  {highlights[vi] && <div className={`ann-hl-badge hl-badge-${highlights[vi]}`}>{highlights[vi]}</div>}
                  <div className="ann-text">{val.text}</div>
                  <div className="ann-actions">
                    <button className="ann-edit" onClick={() => { setSelectedVerse(Number(vi)); setNoteInput(val.text); }}>Edit</button>
                    <button className="ann-del" onClick={() => handleDeleteNote(vi)}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ════════════════════════════════════
           PRAYER WALL TAB
      ════════════════════════════════════ */}
      {tab === 'prayer' && (
        <div className="prayer-wall">
          <div className="section-header">
            <div>
              <div className="section-title">🙏 Prayer Wall</div>
              <div className="section-sub">Lift one another up in prayer · James 5:16</div>
            </div>
            <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
              <select className="bible-select" value={prayerFilter} onChange={e => setPrayerFilter(e.target.value)}>
                <option value="all">All Requests</option>
                <option value="unanswered">Unanswered</option>
                <option value="answered">Answered ✓</option>
                <option value="mine">My Requests</option>
              </select>
              <button className="add-btn" onClick={() => setShowModal(true)}>+ Add Request</button>
            </div>
          </div>
          <div className="prayer-grid">
            {filteredPrayers.map(pr => (
              <div key={pr.id} className={`prayer-card${pr.answered ? ' answered' : ''}`}>
                <div className="prayer-header">
                  <div className="prayer-name">{pr.name}</div>
                  {pr.answered && <div className="answered-badge">✓ Answered</div>}
                </div>
                <div className="prayer-date">{pr.date || (pr.createdAt?.toDate?.()?.toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }))}</div>
                <div className="prayer-body">{pr.request}</div>
                <div className="prayer-tags">
                  {(pr.tags || []).map(t => <span key={t} className="prayer-tag">{t}</span>)}
                </div>
                <div className="prayer-footer">
                  <button className="icon-btn praying-btn" onClick={() => prayForRequest(pr.id)}>
                    🙏 {pr.praying || 0} praying
                  </button>
                  <div className="prayer-actions">
                    {pr.authorUid === userId && (
                      <>
                        <button className="icon-btn" onClick={() => openEditPrayer(pr)} title="Edit">✎</button>
                        <button className="icon-btn" onClick={() => deletePrayer(pr.id)} title="Delete">🗑</button>
                      </>
                    )}
                    <button className="icon-btn" onClick={() => handleMarkAnswered(pr)} title="Mark answered">✓</button>
                    <button className="icon-btn" onClick={() => copyShareLink(pr.id)} title="Share">🔗</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════
           LIVESTREAM TAB
      ════════════════════════════════════ */}
      {tab === 'livestream' && (
        <div className="stream-section">
          <div className="section-header">
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                <div className="section-title">📡 Rumble Livestream</div>
                {isLiveDay && <div className="live-badge">● LIVE TODAY</div>}
              </div>
              <div className="section-sub">Thursdays &amp; Fridays · rumble.com/c/bereannation</div>
            </div>
          </div>

          {/* Rumble embed — connected to your real channel: rumble.com/c/BereanNation
              To embed a SPECIFIC live video, replace src below with the embed URL
              Rumble gives you for that video/stream (Share button → Embed on Rumble.com).
              The channel link in the placeholder always works immediately. */}
          <div className="stream-embed-wrapper">
            {isLiveDay ? (
              <iframe
                className="stream-iframe"
                title="Berean Nation Livestream"
                src="https://rumble.com/embed/v_REPLACE_WITH_LIVE_VIDEO_ID/"
                allowFullScreen
              />
            ) : (
              <div className="stream-placeholder">
                <div className="stream-icon">📡</div>
                <div>Next stream: {dow < 4 ? 'This Thursday' : dow === 5 ? 'Today (Friday)' : 'Next Thursday'}</div>
                <a className="rumble-link" href={RUMBLE_CHANNELS.livestream.url} target="_blank" rel="noopener noreferrer">
                  ↗ Visit Berean Nation on Rumble
                </a>
              </div>
            )}
          </div>

          {/* Live chat */}
          <div className="chat-section-title">💬 Live Chat</div>
          <div className="chat-panel">
            <div className="chat-messages" ref={chatRef}>
              {chatMsgs.map((m, i) => (
                <div key={i} className="chat-msg">
                  <span className={`chat-user${m.user === 'You' ? ' you' : ''}`}>{m.user}: </span>
                  <span className="chat-text">{m.text}</span>
                </div>
              ))}
            </div>
            <div className="chat-input-row">
              <input
                className="chat-input"
                placeholder="Type a message…"
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendChat()}
              />
              <button className="chat-send" onClick={sendChat}>Send</button>
            </div>
          </div>

          {/* Archived streams */}
          <div className="subsection-title">Archived Streams</div>
          <div className="video-grid">
            {ARCHIVED_STREAMS.map(v => (
              <a key={v.id} className="video-card" href={v.link} target="_blank" rel="noopener noreferrer">
                <div className="video-thumb"><span className="play-icon">▶</span></div>
                <div className="video-info">
                  <div className="video-title">{v.title}</div>
                  <div className="video-meta">{v.date} · {v.duration}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* ════════════════════════════════════
           CHURCH FATHERS TAB
      ════════════════════════════════════ */}
      {tab === 'fathers' && (
        <VideoSection
          title="📖 Church Fathers Readings"
          subtitle="Readings from Ignatius, Clement, Justin Martyr, Polycarp, Origen, Irenaeus & more"
          videos={fathersVideos}
          channel={RUMBLE_CHANNELS.fathers}
        />
      )}

      {/* ════════════════════════════════════
           BN NEWS TAB
      ════════════════════════════════════ */}
      {tab === 'news' && (
        <VideoSection
          title="📰 Berean Nation News"
          subtitle="Weekly news, commentary, and special reports from a Berean perspective"
          videos={newsVideos}
          channel={RUMBLE_CHANNELS.news}
        />
      )}

      {/* ── Prayer Modal ── */}
      {showModal && (
        <div className="modal-bg" onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className="modal">
            <div className="modal-title">{editPrayer ? 'Edit Prayer Request' : 'New Prayer Request'}</div>
            <div className="form-group">
              <label className="form-label">Your Name</label>
              <input className="form-input" placeholder="Anonymous" value={prayerForm.name} onChange={e => setPrayerForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label" style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <input type="checkbox" checked={prayerForm.anonymous} onChange={e => setPrayerForm(f => ({ ...f, anonymous: e.target.checked }))} />
                Post anonymously
              </label>
            </div>
            <div className="form-group">
              <label className="form-label">Prayer Request *</label>
              <textarea className="form-textarea" placeholder="Share your heart with the congregation…" value={prayerForm.request} onChange={e => setPrayerForm(f => ({ ...f, request: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Tags (comma-separated)</label>
              <input className="form-input" placeholder="healing, family, provision…" value={prayerForm.tags} onChange={e => setPrayerForm(f => ({ ...f, tags: e.target.value }))} />
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={closeModal}>Cancel</button>
              <button className="btn-primary" onClick={handlePrayerSubmit}>{editPrayer ? 'Update' : 'Post Request'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ── In-app notifications ── */}
      <div className="notif-panel">
        {notifs.map(n => (
          <div key={n.id} className="notif">
            <div className="notif-title">{n.title}</div>
            <div className="notif-body">{n.body}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── VideoSection component ───────────────────────────────
function VideoSection({ title, subtitle, videos, channel }) {
  return (
    <div className="videos-section">
      <div className="section-header">
        <div>
          <div className="section-title">{title}</div>
          <div className="section-sub">{subtitle}</div>
        </div>
      </div>

      {channel && (
        <a className="rumble-link" href={channel.url} target="_blank" rel="noopener noreferrer" style={{ marginBottom: '14px', display: 'inline-flex' }}>
          ↗ Watch all videos on {channel.name} (Rumble)
        </a>
      )}

      {videos.length > 0 ? (
        <div className="video-grid">
          {videos.map(v => (
            <a key={v.id} className="video-card" href={v.link} target="_blank" rel="noopener noreferrer">
              <div className="video-thumb"><span className="play-icon">▶</span></div>
              <div className="video-info">
                <div className="video-title">{v.title}</div>
                <div className="video-meta">{v.date} · {v.duration}</div>
              </div>
            </a>
          ))}
        </div>
      ) : (
        <div className="empty-videos-state">
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>📺</div>
          <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
            No videos listed here yet — check back soon, or watch directly on Rumble above.
          </div>
        </div>
      )}

      {videos.length > 0 && (
        <div className="notif-info-card">
          <div className="notif-info-title">📝 Manual Video List</div>
          <div className="notif-info-body">
            This video list is updated by hand — edit CHURCH_FATHERS_VIDEOS or BN_NEWS_VIDEOS
            near the top of App.js to add new entries. Each card links straight to your real
            Rumble channel.
          </div>
        </div>
      )}
    </div>
  );
}
